// src/internal/hooks/use-side-panel.ts
import * as React from "react";
import isEqual from "just-compare";
import { nanoid } from "nanoid";
import { throttle } from "throttle-debounce";
import { useBoolean, useDisclosure, usePrevious } from "@clara/hooks";
import { isEmptyArray, isNumber } from "@clara/utils";
var flattenPanels = (xs) => {
  return xs.flatMap((x) => {
    if (x == null ? void 0 : x.children) {
      x.children[0].id = nanoid();
      return x.children;
    }
    return x;
  });
};
var initialState = { index: 0, panels: [] };
function useSidePanel() {
  const {
    isOpen,
    onOpen: onOpenSidePanel,
    onClose: onCloseSidePanel
  } = useDisclosure();
  const [state, setState] = React.useState(initialState);
  const prevState = usePrevious(state);
  const [isScrolling, setIsScrolling] = useBoolean();
  const [isScrollAnimated, setScrollAnimated] = useBoolean(false);
  const bodyRef = React.useRef(null);
  React.useEffect(() => {
    if (isEqual(state.panels, prevState == null ? void 0 : prevState.panels) || isEmptyArray(state.panels))
      return;
    onOpenSidePanel();
  });
  React.useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [isScrollAnimated]);
  const onScroll = React.useCallback(
    throttle(100, (event) => {
      const node = event.target;
      if (!(node instanceof HTMLElement))
        return;
      setIsScrolling.toggle(node.scrollTop > 0);
    }),
    [setIsScrolling]
  );
  const actions = React.useMemo(
    () => ({
      /**
       * @deprecated since version 1.1.15.
       * Use onOpen() to go to your next panel. This function will be removed on v2.
       */
      next: () => actions.setPanelAt(state.index + 1),
      prev: () => {
        actions.setPanels(
          state.panels.slice(0, state.panels.length - 1),
          state.index - 1
        );
        actions.setPanelAt(state.index);
      },
      setPanelAt: (newIndex) => {
        const { panels } = state;
        if (!panels.length || state.index === newIndex)
          return;
        const index = newIndex >= 0 ? newIndex % panels.length : panels.length + newIndex % panels.length;
        setState((prev) => ({ ...prev, index }));
      },
      setPanels: (xs, startAt) => setState(({ index: current }) => ({
        index: isNumber(startAt) ? startAt : current,
        panels: flattenPanels(xs)
      })),
      onOpen: (panels, startAt) => {
        actions.setPanels(panels, startAt);
      },
      onClose: (callback) => {
        onCloseSidePanel();
        setState(() => initialState);
        callback == null ? void 0 : callback();
      }
    }),
    [state, onCloseSidePanel]
  );
  const getDrawerProps = React.useCallback(
    (props = {}, ref = null) => ({
      ref,
      ...props,
      variant: "temporary",
      ModalProps: { keepMounted: false },
      isOpen,
      anchor: "right",
      onClose: () => {
        actions.onClose();
        if (props == null ? void 0 : props.onClose) {
          props.onClose();
        }
      }
    }),
    [isOpen, actions]
  );
  return {
    // prop getter
    getDrawerProps,
    // state
    isOpen,
    isScrolling,
    onScroll,
    ...state,
    current: state.panels[state.index],
    isScrollAnimated,
    setScrollAnimated,
    bodyRef,
    // actions
    ...actions
  };
}

export {
  useSidePanel
};
